class FileTransferService {
  constructor(webrtcService) {
    this.webrtc = webrtcService;
    this.files = [];
    this.currentFileIndex = 0;
    this.chunkSize = 16384; // 16KB chunks
    this.onProgressCallback = null;
    this.onCompleteCallback = null;
    this.receivedFiles = [];
    this.currentReceivingFile = null;
  }

  async sendFiles(files) {
    this.files = Array.from(files);
    this.currentFileIndex = 0;

    // Send metadata first
    const metadata = this.files.map(file => ({
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    }));

    this.webrtc.sendData(JSON.stringify({
      type: 'metadata',
      files: metadata
    }));

    // Wait a bit for receiver to prepare
    await new Promise(resolve => setTimeout(resolve, 100));

    // Start sending files
    await this.sendNextFile();
  }

  async sendNextFile() {
    if (this.currentFileIndex >= this.files.length) {
      // All files sent
      this.webrtc.sendData(JSON.stringify({ type: 'complete' }));
      if (this.onCompleteCallback) {
        this.onCompleteCallback();
      }
      return;
    }

    const file = this.files[this.currentFileIndex];
    const reader = new FileReader();
    let offset = 0;

    reader.onload = async (e) => {
      const chunk = e.target.result;
      
      // Send chunk
      this.webrtc.sendData(chunk);

      offset += chunk.byteLength;

      // Calculate progress
      const fileProgress = (offset / file.size) * 100;
      const totalProgress = ((this.currentFileIndex + (offset / file.size)) / this.files.length) * 100;

      if (this.onProgressCallback) {
        this.onProgressCallback({
          fileIndex: this.currentFileIndex,
          fileName: file.name,
          fileProgress,
          totalProgress,
          bytesTransferred: offset,
          totalBytes: file.size
        });
      }

      // Read next chunk or move to next file
      if (offset < file.size) {
        readNextChunk();
      } else {
        // File complete, send marker
        this.webrtc.sendData(JSON.stringify({
          type: 'file-complete',
          fileIndex: this.currentFileIndex
        }));

        this.currentFileIndex++;
        await new Promise(resolve => setTimeout(resolve, 50));
        await this.sendNextFile();
      }
    };

    const readNextChunk = () => {
      const slice = file.slice(offset, offset + this.chunkSize);
      reader.readAsArrayBuffer(slice);
    };

    readNextChunk();
  }

  startReceiving() {
    let receivedChunks = [];
    let currentFileMeta = null;
    let fileIndex = 0;

    this.webrtc.onMessage((data) => {
      // Check if it's JSON metadata
      if (typeof data === 'string') {
        try {
          const message = JSON.parse(data);

          if (message.type === 'metadata') {
            this.receivedFiles = message.files.map(meta => ({
              ...meta,
              data: [],
              received: 0
            }));
            currentFileMeta = this.receivedFiles[0];
            console.log('Receiving files:', this.receivedFiles);
          } else if (message.type === 'file-complete') {
            // Save current file
            this.saveReceivedFile(fileIndex);
            receivedChunks = [];
            fileIndex++;
            currentFileMeta = this.receivedFiles[fileIndex];
          } else if (message.type === 'complete') {
            console.log('✅ All files received');
            if (this.onCompleteCallback) {
              this.onCompleteCallback(this.receivedFiles);
            }
          }
        } catch (e) {
          // Not JSON, ignore
        }
      } else {
        // Binary data (file chunk)
        if (currentFileMeta) {
          receivedChunks.push(new Uint8Array(data));
          currentFileMeta.received += data.byteLength;

          const fileProgress = (currentFileMeta.received / currentFileMeta.size) * 100;
          const totalProgress = ((fileIndex + (currentFileMeta.received / currentFileMeta.size)) / this.receivedFiles.length) * 100;

          if (this.onProgressCallback) {
            this.onProgressCallback({
              fileIndex,
              fileName: currentFileMeta.name,
              fileProgress,
              totalProgress,
              bytesTransferred: currentFileMeta.received,
              totalBytes: currentFileMeta.size
            });
          }

          currentFileMeta.data = receivedChunks;
        }
      }
    });
  }

  saveReceivedFile(fileIndex) {
    const fileMeta = this.receivedFiles[fileIndex];
    if (!fileMeta || !fileMeta.data.length) return;

    // Combine chunks
    const totalSize = fileMeta.data.reduce((acc, chunk) => acc + chunk.length, 0);
    const combined = new Uint8Array(totalSize);
    let offset = 0;

    fileMeta.data.forEach(chunk => {
      combined.set(chunk, offset);
      offset += chunk.length;
    });

    // Create blob and download
    const blob = new Blob([combined], { type: fileMeta.type });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = fileMeta.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log(`✅ Saved file: ${fileMeta.name}`);
  }

  onProgress(callback) {
    this.onProgressCallback = callback;
  }

  onComplete(callback) {
    this.onCompleteCallback = callback;
  }
}

export default FileTransferService;
